/**
 * Session lifecycle calls the auth provider makes against the platform API.
 *
 * These are the pre-shell operations — probing for a cookie session, the
 * cross-origin exchange bounce, and logout — issued straight over the GraphQL
 * endpoint rather than through the platform client, because they run before a
 * platform runtime exists and must never pull one into the auth bundle.
 *
 * @module auth/session-client
 */

import { wireErrors, wireStatus } from "@/platform/api-error";
// Generated string constants only. The module has no runtime of its own, so
// naming fields from it costs the auth bundle nothing the docstring above warns
// against.
import {
  LOGOUT_RESULT_FIELDS,
  SESSION_EXCHANGE_RESULT_FIELDS,
} from "@/platform/graphql-fragments.generated";
import type { PlatformResult } from "@/platform/platform-contract.generated";

/** What {@link probeCookieSession} learned from the API about the cookie. */
export interface CookieSessionProbe {
  /** Whether the request carried a credential the API accepted as identifying someone. */
  authenticated: boolean;
  /** The user the cookie names, when the API returned a session. */
  userId: string | null;
}

/** An executed operation's data, and the failure it reported instead. */
interface OperationResult {
  /** The HTTP status, which is 200 for anything raised while executing. */
  status: number;
  /** The `data` entry, when the operation produced one. */
  data?: Record<string, unknown>;
  /** The platform status the operation failed with, when it failed. */
  failedWith?: number;
}

/** Execute one GraphQL operation with the browser's credentials. */
function post(
  apiBaseUrl: string,
  query: string,
  variables?: Record<string, unknown>,
  accessToken?: string,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/graphql-response+json, application/json",
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return fetch(`${apiBaseUrl}/graphql`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(variables === undefined ? { query } : { query, variables }),
  });
}

/**
 * Execute an operation and report what it produced or how it failed.
 *
 * An operation that fails during execution answers 200 with an `errors` entry,
 * per the GraphQL specification, so the platform status has to be read out of
 * the entry's `extensions` rather than off the status line.
 *
 * @param apiBaseUrl - Base URL of the platform API.
 * @param query - The GraphQL document.
 * @returns The status, the data, and the platform status of any failure.
 */
async function execute(apiBaseUrl: string, query: string): Promise<OperationResult> {
  const response = await post(apiBaseUrl, query);
  const payload = (await response.json().catch(() => undefined)) as
    { data?: Record<string, unknown> } | undefined;
  const [failure] = wireErrors(payload);
  let failedWith: number | undefined;
  if (failure) {
    failedWith = wireStatus(failure) ?? (response.ok ? 500 : response.status);
  } else {
    failedWith = response.ok ? undefined : response.status;
  }

  return {
    status: response.status,
    data: payload?.data,
    failedWith,
  };
}

/**
 * Ask the API whether the browser is carrying a usable session cookie.
 *
 * The cookie is `HttpOnly`, so script cannot read it; the only way to know
 * whether one is live is to make a credentialed request and look at the answer.
 * The `getCurrentSession` operation is that request — it is the same one the
 * workspace loads from, so a success here means the workspace will load too.
 *
 * Only a 401 means "no usable credential". Any other failure (a tenant the
 * caller cannot reach, a server error) means the cookie itself was accepted,
 * and reporting that as signed-out would show a sign-in button that cannot fix
 * anything — the workspace surfaces the real reason instead.
 *
 * @param apiBaseUrl - Base URL of the platform API.
 * @returns Whether a credential was accepted, and the user it names.
 */
export async function probeCookieSession(apiBaseUrl: string): Promise<CookieSessionProbe> {
  try {
    // Only the caller's id, because that is all the probe reports. Asking for
    // the whole session would make the shell's cheapest check as expensive as
    // its load, and every field it did not read would still be resolved.
    const result = await execute(apiBaseUrl, "query { getCurrentSession { user { id } } }");
    if (result.failedWith === 401) {
      return { authenticated: false, userId: null };
    }
    if (result.failedWith !== undefined) {
      return { authenticated: true, userId: null };
    }
    const session = result.data?.getCurrentSession as
      { user?: Pick<PlatformResult<"getCurrentSession">["user"], "id"> } | undefined;
    return { authenticated: true, userId: session?.user?.id ?? null };
  } catch {
    // A network failure is not evidence either way, and guessing "signed in"
    // would strand the caller on a workspace that cannot load.
    return { authenticated: false, userId: null };
  }
}

/**
 * Redeem a sign-in-origin exchange code into this host's session cookie.
 *
 * The response's `Set-Cookie` is the entire point; the body is ignored.
 * Network failures propagate for the caller to swallow, matching how the
 * bounce has always degraded to the MSAL check.
 *
 * @param apiBaseUrl - Base URL of the platform API.
 * @param code - The single-use exchange code from the URL.
 */
export async function redeemExchangeCode(apiBaseUrl: string, code: string): Promise<void> {
  await post(
    apiBaseUrl,
    `mutation ($code: String) { exchangeSession(code: $code) { ${SESSION_EXCHANGE_RESULT_FIELDS} } }`,
    { code },
  );
}

/**
 * Mint an exchange code on the sign-in origin for a return URL.
 *
 * @param apiBaseUrl - Base URL of the platform API.
 * @param returnUrl - Where the caller is heading.
 * @param accessToken - The caller's Entra access token.
 * @returns The code, or undefined when the API refuses.
 */
export async function mintExchangeCode(
  apiBaseUrl: string,
  returnUrl: string,
  accessToken: string,
): Promise<string | undefined> {
  const response = await post(
    apiBaseUrl,
    `mutation ($returnUrl: String) { exchangeSession(returnUrl: $returnUrl) { ${SESSION_EXCHANGE_RESULT_FIELDS} } }`,
    { returnUrl },
    accessToken,
  );
  const payload = (await response.json().catch(() => undefined)) as
    { data?: { exchangeSession?: PlatformResult<"exchangeSession"> | null } } | undefined;
  // A refusal is a failed operation, not a bad response: the caller falls back
  // to a direct navigation either way, so there is nothing to distinguish.
  if (!response.ok || wireErrors(payload).length > 0) {
    return undefined;
  }
  return payload?.data?.exchangeSession?.code ?? undefined;
}

/**
 * Revoke the caller's session cookie and earlier session tokens.
 *
 * Network failures propagate for the caller to swallow; the picker or reload
 * that follows re-establishes identity either way.
 *
 * @param apiBaseUrl - Base URL of the platform API.
 */
export async function revokeSession(apiBaseUrl: string): Promise<void> {
  await post(apiBaseUrl, `mutation { logoutSession { ${LOGOUT_RESULT_FIELDS} } }`);
}
